import React, { useEffect, useState } from 'react';
import { Box, Paper, Typography, Button, CircularProgress } from '@mui/material';

interface DeprecationNoticeProps {
  /** Where to send users. */
  redirectUrl: string;
  /** Seconds to show the notice before auto-redirecting. */
  delaySeconds?: number;
}

/**
 * Full-screen retirement notice for the legacy TIL system. Time in Lieu has
 * moved to myHR; this shows a short message then redirects. Rendered before the
 * router/auth so the old app is unreachable and no login is required.
 */
const DeprecationNotice: React.FC<DeprecationNoticeProps> = ({ redirectUrl, delaySeconds = 6 }) => {
  const [secondsLeft, setSecondsLeft] = useState(delaySeconds);

  useEffect(() => {
    const tick = setInterval(() => setSecondsLeft(s => (s > 0 ? s - 1 : 0)), 1000);
    const redirect = setTimeout(() => {
      window.location.replace(redirectUrl);
    }, delaySeconds * 1000);
    return () => {
      clearInterval(tick);
      clearTimeout(redirect);
    };
  }, [redirectUrl, delaySeconds]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
        bgcolor: 'background.default',
      }}
    >
      <Paper elevation={3} sx={{ p: { xs: 3, sm: 5 }, maxWidth: 560, textAlign: 'center' }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700, color: 'primary.main' }}>
          This system has moved
        </Typography>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>
          Time in Lieu is now managed in myHR
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          The old TIL system has been retired. Your balance, requests and approvals
          have all moved across to myHR. Please use myHR from now on — this site is
          no longer accepting requests.
        </Typography>
        <Button
          variant="contained"
          size="large"
          href={redirectUrl}
          sx={{ mb: 2, px: 4 }}
        >
          Go to myHR now
        </Button>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, color: 'text.secondary' }}>
          <CircularProgress size={16} thickness={5} />
          <Typography variant="body2">
            Redirecting automatically in {secondsLeft}s…
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default DeprecationNotice;
